import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/format";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Handle Google OAuth callback — session_id arrives in URL hash
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      const session_id = new URLSearchParams(hash.replace("#", "")).get("session_id");
      if (session_id) {
        api.post("/auth/google/session", { session_id })
          .then(({ data }) => {
            localStorage.setItem("nme_token", data.token);
            setUser(data.user);
            // Clean the hash
            window.history.replaceState({}, "", window.location.pathname + window.location.search);
          })
          .catch(() => {
            // fall through to standard check below
          })
          .finally(() => setLoading(false));
        return;
      }
    }
    const token = localStorage.getItem("nme_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => {
        localStorage.removeItem("nme_token");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("nme_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("nme_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("nme_token");
    setUser(null);
  }, []);

  const setSession = useCallback((token, authUser) => {
    localStorage.setItem("nme_token", token);
    setUser(authUser);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
